// src/services/membershipService.js
//
// Phase 7 — Tenant/site/role membership model.
//
// Membership lives in the ROOT collection user_roles/{email} (read at login,
// before any site is known). The document shape is:
//
//   user_roles/{email} = {
//     globalRole: "owner" | undefined,            // cross-company superuser
//     memberships: {
//       [tenantId]: {
//         tenantRole: "admin" | undefined,         // manage this whole tenant
//         sites: { [siteId]: "<siteRole>" }        // per-site role
//       }
//     },
//     role: "<legacy flat role>"                    // tolerated for back-compat
//   }
//
// IMPORTANT: this service only ever reads/writes tenant + site METADATA and the
// user_roles docs. It never touches any patient/PHI collection.

import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  deleteField,
} from "firebase/firestore";
import {
  getUserRolesPath,
  getTenantsCollectionPath,
  getTenantDocPathFor,
  getSitesCollectionPathFor,
  getSiteDocPathFor,
} from "./firestorePaths";
import { DEFAULT_TENANT_ID, DEFAULT_SITE_ID } from "./activeTenant";

// Roles that can be assigned to a user at a single site.
export const SITE_ROLES = [
  "admin",
  "coordinator",
  "reception",
  "provider",
  "monitor",
  "referral",
];

const lc = (email) => String(email || "").trim().toLowerCase();

// ---------------------------------------------------------------------------
// Read + normalize
// ---------------------------------------------------------------------------

/** Normalize a raw user_roles doc into a predictable shape. */
export const normalizeMembership = (data) => {
  if (!data) return { globalRole: null, memberships: {}, legacyRole: null };
  return {
    globalRole: data.globalRole || null,
    memberships: data.memberships || {},
    legacyRole: data.role || null,
  };
};

/** Load + normalize a user's membership. Returns null if no doc exists. */
export const loadMembership = async (email) => {
  const snap = await getDoc(doc(db, getUserRolesPath(), lc(email)));
  if (!snap.exists()) return null;
  return normalizeMembership(snap.data());
};

/**
 * Derive an access summary from a normalized membership. Falls back to the
 * legacy flat role by synthesizing a membership at the default tenant/site, so
 * users that haven't been migrated yet still work.
 */
export const deriveAccess = (normalized) => {
  const m = normalized || { globalRole: null, memberships: {}, legacyRole: null };
  const isOwner = m.globalRole === "owner";

  let memberships = m.memberships || {};

  // Back-compat: no memberships but a legacy flat role -> default tenant/site.
  if (Object.keys(memberships).length === 0 && m.legacyRole) {
    memberships = {
      [DEFAULT_TENANT_ID]: {
        tenantRole: m.legacyRole === "admin" ? "admin" : undefined,
        sites: { [DEFAULT_SITE_ID]: m.legacyRole },
      },
    };
  }

  const allowedTenantIds = Object.keys(memberships);

  const isTenantAdmin = (tenantId) =>
    isOwner || memberships[tenantId]?.tenantRole === "admin";

  const sitesForTenant = (tenantId) =>
    Object.keys(memberships[tenantId]?.sites || {});

  const roleFor = (tenantId, siteId) => {
    if (!tenantId || !siteId) return null;
    return memberships[tenantId]?.sites?.[siteId] || null;
  };

  const canAccessSite = (tenantId, siteId) =>
    isOwner ||
    isTenantAdmin(tenantId) ||
    Boolean(roleFor(tenantId, siteId));

  // A sensible landing context: first tenant + first site the user has.
  let primary = null;
  const firstTenant = allowedTenantIds[0];
  if (firstTenant) {
    const firstSite = sitesForTenant(firstTenant)[0] || null;
    primary = {
      tenantId: firstTenant,
      siteId: firstSite,
      role: firstSite ? roleFor(firstTenant, firstSite) : null,
    };
  }

  return {
    isOwner,
    memberships,
    allowedTenantIds,
    isTenantAdmin,
    sitesForTenant,
    roleFor,
    canAccessSite,
    primary,
  };
};

/**
 * The single role a user effectively has in a given tenant/site context. This
 * is the canonical resolver used for BOTH post-login routing and route guards,
 * so they can never disagree:
 *   - portal owner           -> "owner"
 *   - tenant admin of tenant -> "admin"  (even with no explicit site role)
 *   - otherwise              -> their site role for (tenantId, siteId), or null
 *
 * The legacy flat `user_roles.role` is intentionally NOT consulted here to
 * override a membership: that is the heart of site isolation (a root "admin"
 * who is only a coordinator on this site is a coordinator here). deriveAccess
 * already folds the legacy role into a default-site membership for users who
 * have not been migrated yet, so unmigrated accounts keep working.
 */
export const effectiveRole = (access, tenantId, siteId) => {
  if (!access) return null;
  if (access.isOwner) return "owner";
  if (access.isTenantAdmin(tenantId)) return "admin";
  return access.roleFor(tenantId, siteId) || null;
};

// ---------------------------------------------------------------------------
// Tenant (company) management
// ---------------------------------------------------------------------------
export const listTenants = async () => {
  const snap = await getDocs(collection(db, getTenantsCollectionPath()));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getTenant = async (tenantId) => {
  const snap = await getDoc(doc(db, getTenantDocPathFor(tenantId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Create/update a tenant (merge — never clobbers existing fields). */
export const upsertTenant = async (tenantId, data = {}) => {
  await setDoc(
    doc(db, getTenantDocPathFor(tenantId)),
    {
      name: data.name ?? tenantId,
      displayName: data.displayName ?? data.name ?? tenantId,
      status: data.status ?? "active",
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

// ---------------------------------------------------------------------------
// Site management
// ---------------------------------------------------------------------------
export const listSites = async (tenantId) => {
  const snap = await getDocs(collection(db, getSitesCollectionPathFor(tenantId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getSite = async (tenantId, siteId) => {
  const snap = await getDoc(doc(db, getSiteDocPathFor(tenantId, siteId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Create/update a site (merge). A brand-new site starts with no patient data. */
export const upsertSite = async (tenantId, siteId, data = {}) => {
  await setDoc(
    doc(db, getSiteDocPathFor(tenantId, siteId)),
    {
      name: data.name ?? siteId,
      code: data.code ?? siteId,
      status: data.status ?? "active",
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

// ---------------------------------------------------------------------------
// User membership management (non-destructive deep merges)
// ---------------------------------------------------------------------------
export const listUsers = async () => {
  const snap = await getDocs(collection(db, getUserRolesPath()));
  return snap.docs.map((d) => ({
    email: d.id,
    ...normalizeMembership(d.data()),
  }));
};

/** Assign (or change) a user's role at one site. Preserves all other access. */
export const assignUserToSite = async (email, tenantId, siteId, role) => {
  await setDoc(
    doc(db, getUserRolesPath(), lc(email)),
    { memberships: { [tenantId]: { sites: { [siteId]: role } } } },
    { merge: true }
  );
};

/** Remove a user's access to one site (leaves every other membership intact). */
export const removeUserFromSite = async (email, tenantId, siteId) => {
  await updateDoc(doc(db, getUserRolesPath(), lc(email)), {
    [`memberships.${tenantId}.sites.${siteId}`]: deleteField(),
  });
};

/** Grant/revoke tenant-admin for a user on a tenant. */
export const setTenantAdmin = async (email, tenantId, isAdmin) => {
  await setDoc(
    doc(db, getUserRolesPath(), lc(email)),
    { memberships: { [tenantId]: { tenantRole: isAdmin ? "admin" : null } } },
    { merge: true }
  );
};

/** Grant/revoke the cross-company owner role. */
export const setGlobalRole = async (email, isOwner) => {
  await setDoc(
    doc(db, getUserRolesPath(), lc(email)),
    { globalRole: isOwner ? "owner" : null },
    { merge: true }
  );
};
